import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import TextReveal from "../components/TextReveal/TextReveal";
import ExperienceScene from "../components/Morph/scenes/ExperienceScene";
import ExperiencePhoneScene from "../components/Morph/scenes/ExperiencePhoneScene";
import { usePhoneScene, useStoryMode } from "../hooks/useStoryMode";
import { EXPERIENCES } from "../data/experience";
import { DUR, EASE, STAGGER, VIEWPORT } from "../lib/motion";
import "./Experience.css";

/**
 * Work history, between Profile and Work. In story mode it plays as the
 * "Fold & Branch" scene; everywhere else it is the plain timeline that used to
 * live at the bottom of About's bento grid.
 */
export default function Experience() {
    const story = useStoryMode();
    const phone = usePhoneScene();
    if (!EXPERIENCES.length) return null;
    if (story) return <ExperienceScene />;
    // Pilot: Experience is the first scene with a portrait version.
    if (phone) return <ExperiencePhoneScene />;
    return <ExperienceTimeline />;
}

/**
 * The plain (phone) layout. Same ideas as the desktop scene, in a form that
 * suits free scrolling: the timeline draws itself down as you scroll through
 * it, and each role's card unfolds from its top edge like a hinged panel.
 */
function ExperienceTimeline() {
    const lineRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: lineRef, offset: ["start 80%", "end 60%"] });
    const draw = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

    return (
        <div className="experience-page">
            <div className="experience-container">
                <div className="experience-header">
                    <TextReveal as="h1" className="experience-title" mutedFrom={1}>
                        Work Experience
                    </TextReveal>
                    <TextReveal as="p" className="experience-subtitle" delay={0.14}>
                        Two roles so far, from backend APIs to owning the full stack.
                    </TextReveal>
                </div>

                <div className="timeline" ref={lineRef}>
                    <motion.span className="timeline-progress" style={{ scaleY: draw }} aria-hidden="true" />
                    <span className="timeline-runner" aria-hidden="true">
                        <i />
                    </span>
                    {EXPERIENCES.map((exp, idx) => (
                        <motion.div
                            key={exp.company}
                            className="timeline-item"
                            initial={{ rotateX: -78, opacity: 0 }}
                            whileInView={{ rotateX: 0, opacity: 1 }}
                            viewport={VIEWPORT}
                            transition={{ duration: DUR.base, delay: idx * STAGGER * 2, ease: EASE }}
                        >
                            <div className="timeline-marker" style={{ "--i": idx } as React.CSSProperties} />
                            <div className="timeline-content">
                                <div className="exp-head">
                                    <h3 className="exp-title">{exp.title}</h3>
                                    <span className="exp-period mono">{exp.period}</span>
                                </div>
                                <p className="exp-company">{exp.company}</p>
                                <p className="exp-description">{exp.description}</p>
                                <ul className="exp-highlights">
                                    {exp.responsibilities.map((r) => (
                                        <li key={r}>
                                            <span className="exp-bullet" aria-hidden="true" />
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
